import { useMutation } from '@tanstack/react-query';
import { AxiosError, isAxiosError } from 'axios';
import React from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';

import { bulkEditItems } from 'apis/restaurant-api/merchant-admin/menu2/api';
import {
  BulkActionBasePayload,
  Category,
  MenuContainer,
  Option,
  OptionBindingAttrs,
} from 'apis/restaurant-api/merchant-admin/menu2/types';
import { reportBulkAction } from 'modules/menu/behavioral';
import { useAddOptionsTracking } from 'modules/menu/hooks/telemetryHooks';
import { OptionBindingsModal } from 'modules/menu/items/components/OptionBinding';
import { buildOptionBindingCreatePayload } from 'modules/menu/items/components/OptionBinding/utils';
import useMenuData from 'modules/menu/useMenuData';

import DetailedErrorList from './DetailedErrorList';
import useBulkSelection from '../../useBulkSelection';
import { buildBulkActionBasePayload } from '../../utils';
import BulkActionModalTitle from '../BulkActionModalTitle';

type Props = {
  categories: Category[];
  menuId: string;
  onClearItemSelection: () => void;
  onClose: () => void;
  selectedItemIds: Set<string>;
};

type BulkAddOptionsToItemsPayload = BulkActionBasePayload & {
  add_option_bindings: OptionBindingAttrs[];
};

const buildPayload = (
  categories: Category[],
  selectedItemIds: Set<string>,
  selectedOptionIds: Set<string>,
  allOptions: Option[],
): BulkAddOptionsToItemsPayload => {
  return {
    ...buildBulkActionBasePayload(categories, selectedItemIds),
    add_option_bindings: allOptions
      .filter(option => selectedOptionIds.has(option.id))
      .map(option => buildOptionBindingCreatePayload(option)),
  };
};

type ErrorResponseWithDetails = AxiosError<{
  error: {
    description: string;
    details: {
      item_ids: {
        [itemId: string]: {
          option_ids: {
            [optionId: string]: string;
          };
        };
      };
    };
  };
  modified_menu_container: MenuContainer;
}>;

const isErrorResponseWithDetails = (error: unknown): error is ErrorResponseWithDetails =>
  isAxiosError(error) &&
  !!error.response?.data.error?.details?.item_ids &&
  !!error.response?.data.modified_menu_container;

const BulkAddOptionsToItems = ({ categories, menuId, onClearItemSelection, onClose, selectedItemIds }: Props) => {
  const { options, updateMenuData } = useMenuData();

  const {
    selectedIds: selectedOptionIds,
    select: selectOption,
    deselect: deselectOption,
    clearSelection: clearOptionSelection,
  } = useBulkSelection();

  const trackAddOptions = useAddOptionsTracking([...selectedItemIds], [...selectedOptionIds]);

  const handleSubmit = useMutation((payload: BulkAddOptionsToItemsPayload) => bulkEditItems(menuId, payload), {
    onMutate: () => {
      reportBulkAction(selectedItemIds.size, 'BULK_ADD_OPTIONS');
    },
    onSuccess: (data: MenuContainer) => {
      updateMenuData({ actionType: 'updateMenuContainer', payload: data });
      clearOptionSelection();
      onClearItemSelection();
      toast.success(
        <FormattedMessage
          id="menu.bulkActions.addOptionsToItems.successDescription"
          values={{ count: selectedOptionIds.size }}
        />,
      );
      trackAddOptions();
    },
    onError: e => {
      let toastContent = (
        <FormattedMessage
          id="menu.bulkActions.addOptionsToItems.errorDescription"
          values={{ optionCount: selectedOptionIds.size, itemCount: selectedItemIds.size }}
        />
      );

      if (isErrorResponseWithDetails(e) && e.response) {
        toastContent = <DetailedErrorList itemIdsWithErrors={e.response.data.error.details.item_ids} />;
        updateMenuData({ actionType: 'updateMenuContainer', payload: e.response.data.modified_menu_container });
        clearOptionSelection();
        onClearItemSelection();
      }

      toast.error(toastContent, { duration: Infinity });
    },
    onSettled: () => {
      onClose();
    },
  });

  return (
    <OptionBindingsModal
      loading={handleSubmit.isLoading}
      mode="bulkAction"
      onClose={onClose}
      onDeselectOption={deselectOption}
      isOpen
      onSelectOption={selectOption}
      onDone={() => {
        handleSubmit.mutate(buildPayload(categories, selectedItemIds, selectedOptionIds, options));
      }}
      selectedOptionIds={selectedOptionIds}
      title={
        <BulkActionModalTitle
          count={selectedItemIds.size}
          title={<FormattedMessage id="menu.bulkActions.addOptionsToItems.addOptions" />}
        />
      }
    />
  );
};

export default BulkAddOptionsToItems;
